import {isNil} from 'ramda';

import type {
  CheckoutProfile,
  CheckoutProfileRule,
  CheckoutProfileRulePreview,
  CheckoutProfileRuleWrite,
} from '../types';
import type {CheckoutProfileMockVariant} from './checkoutProfilesFixtures';

const contains = (
  values: CheckoutProfileRuleWrite['conditions']['serviceCaseTypeIds'],
  id: string
) => isNil(values) || values.includes(id);

function matchesCheckoutProfileVariant(
  rule: CheckoutProfileRuleWrite,
  variant: CheckoutProfileMockVariant
) {
  if (
    !variant.isActive ||
    rule.branchId !== variant.branchId ||
    rule.sourceType !== variant.sourceType
  ) {
    return false;
  }
  const conditions = rule.conditions;
  return rule.sourceType === 'service-order'
    ? contains(conditions.serviceCaseTypeIds, variant.parentTypeId) &&
        contains(conditions.serviceOrderTypeIds, variant.typeId) &&
        contains(conditions.serviceOrderVariantIds, variant.id)
    : contains(conditions.authorizationProfileIds, variant.parentTypeId) &&
        contains(conditions.directSaleTypeIds, variant.typeId) &&
        contains(conditions.directSaleVariantIds, variant.id);
}

export function getCheckoutProfileRuleConflicts(
  draft: CheckoutProfileRuleWrite & {id?: string},
  rules: CheckoutProfileRule[],
  profiles: Map<string, CheckoutProfile>,
  variants: CheckoutProfileMockVariant[]
) {
  return rules
    .filter(
      (rule) =>
        rule.id !== draft.id &&
        rule.isActive &&
        rule.priority === draft.priority &&
        rule.branchId === draft.branchId &&
        rule.sourceType === draft.sourceType &&
        profiles.get(rule.checkoutProfileId)?.typeOfSale ===
          profiles.get(draft.checkoutProfileId)?.typeOfSale &&
        variants.some(
          (variant) =>
            matchesCheckoutProfileVariant(draft, variant) &&
            matchesCheckoutProfileVariant(rule, variant)
        )
    )
    .map(({id, name, priority}) => ({id, name, priority}));
}

export function previewCheckoutProfileRules(
  draft: CheckoutProfileRuleWrite & {id?: string},
  rules: CheckoutProfileRule[],
  profiles: Map<string, CheckoutProfile>,
  variants: CheckoutProfileMockVariant[]
): CheckoutProfileRulePreview {
  const draftId = draft.id ?? '00000000-0000-4000-8000-000000000000';
  const createdAt =
    rules.find((rule) => rule.id === draft.id)?.createdAt ?? '9999-12-31T23:59:59.999Z';
  const candidates = [
    ...rules.filter((rule) => rule.id !== draft.id),
    {...draft, id: draftId, createdAt},
  ];
  const winner = (
    variant: CheckoutProfileMockVariant,
    typeOfSale: CheckoutProfile['typeOfSale']
  ) => {
    const rule = candidates
      .filter(
        (candidate) =>
          candidate.isActive &&
          profiles.get(candidate.checkoutProfileId)?.isActive &&
          profiles.get(candidate.checkoutProfileId)?.typeOfSale === typeOfSale &&
          matchesCheckoutProfileVariant(candidate, variant)
      )
      .sort(
        (first, second) =>
          second.priority - first.priority ||
          first.createdAt.localeCompare(second.createdAt) ||
          first.id.localeCompare(second.id)
      )[0];
    return rule ? {id: rule.id, name: rule.name, isThisRule: rule.id === draftId} : null;
  };
  return {
    conflicts: getCheckoutProfileRuleConflicts(draft, rules, profiles, variants),
    matchingVariants: variants
      .filter((variant) => matchesCheckoutProfileVariant(draft, variant))
      .map((variant) => ({
        variantId: variant.id,
        variantName: variant.name,
        ...(variant.sourceType === 'service-order'
          ? {serviceCaseTypeName: variant.parentTypeName}
          : {authorizationProfileName: variant.parentTypeName}),
        typeName: variant.typeName,
        winningRuleDefault: winner(variant, 'DEFAULT'),
        winningRuleInternal: winner(variant, 'INTERNAL'),
      })),
  };
}
